import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { uid } from "../lib/storage";
import { isMain } from "../lib/permissions";
import ProductFormFields from "./catalog/ProductFormFields";
import {
  EMPTY_PRODUCT,
  buildProductPayload,
  validateProduct,
} from "./catalog/productFormUtils";

function initialProductForm(product, suppliers, categories, user) {
  const firstCat = categories[0]?.name || "";
  if (product) return { ...EMPTY_PRODUCT, category: firstCat, ...product };
  const forcedRest = !isMain(user) && user?.restaurant
    ? [user.restaurant]
    : ["restaurant_1", "restaurant_2"];
  const owner = isMain(user) ? "global" : user?.restaurant || "global";
  return {
    ...EMPTY_PRODUCT,
    category: firstCat,
    supplierId: suppliers[0]?.id || "",
    assignedRestaurants: forcedRest,
    ownerRestaurant: owner,
  };
}

export function ProductDialog({ open, onOpenChange, product, suppliers, user, onSave, categories = [] }) {
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [imgMode, setImgMode] = useState("url");

  useEffect(() => {
    if (!open) return;
    setForm(initialProductForm(product, suppliers, categories, user));
    setImgMode(product?.image?.startsWith("data:") ? "upload" : "url");
  }, [open, product, suppliers, user, categories]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const toggleIn = (key, value) => setForm((f) => {
    const arr = f[key] || [];
    return { ...f, [key]: arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value] };
  });

  const handleSubmit = () => {
    const error = validateProduct(form);
    if (error) return toast.error(error);
    const saved = onSave(buildProductPayload(form, product), !!product);
    if (saved !== false) onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[92vh] overflow-y-auto bg-[#FBF8F1] border-[#E6E0D4]">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl text-[#0B1F3A]">
            {product ? "Edit product" : "New product"}
          </DialogTitle>
          <DialogDescription className="text-[#0B1F3A]/65">
            Scope the product to restaurants and areas so the right people see it.
          </DialogDescription>
        </DialogHeader>

        <ProductFormFields
          form={form}
          imgMode={imgMode}
          set={set}
          toggleIn={toggleIn}
          suppliers={suppliers}
          categories={categories}
          user={user}
          canPickAnyRestaurant={isMain(user)}
        />

        <DialogFooter className="pt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-[#0B1F3A]">Cancel</Button>
          <Button onClick={handleSubmit} className="bg-[#0B1F3A] hover:bg-[#11294A] text-white">
            {product ? "Save changes" : "Create product"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const EMPTY_SUPPLIER = { name: "", type: "delivered", lead: "" };

function buildSupplierPayload(form, existing) {
  return {
    ...form,
    id: existing?.id || uid("sup"),
    name: form.name.trim(),
    lead: form.lead.trim() || "—",
  };
}

export function SupplierDialog({ open, onOpenChange, supplier, onSave }) {
  const [form, setForm] = useState(EMPTY_SUPPLIER);
  useEffect(() => { if (open) setForm(supplier ? { ...supplier } : EMPTY_SUPPLIER); }, [open, supplier]);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const handleSubmit = () => {
    if (!form.name.trim()) return toast.error("Name is required");
    onSave(buildSupplierPayload(form, supplier), !!supplier);
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-[#FBF8F1] border-[#E6E0D4]">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl text-[#0B1F3A]">
            {supplier ? "Edit vendor" : "New vendor"}
          </DialogTitle>
          <DialogDescription className="text-[#0B1F3A]/65">
            Delivered vendors drop off on a schedule. Direct vendors are picked up in person.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div>
            <Label className="text-[10.5px] tracking-eyebrow uppercase text-[#0B1F3A]/60">Name</Label>
            <Input value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g., MPG" className="mt-1 bg-white border-[#E6E0D4]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10.5px] tracking-eyebrow uppercase text-[#0B1F3A]/60">Type</Label>
              <Select value={form.type} onValueChange={(v) => set({ type: v })}>
                <SelectTrigger className="mt-1 bg-white border-[#E6E0D4]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="delivered">Delivered</SelectItem>
                  <SelectItem value="direct">Direct / pickup</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10.5px] tracking-eyebrow uppercase text-[#0B1F3A]/60">Lead / schedule</Label>
              <Input value={form.lead} onChange={(e) => set({ lead: e.target.value })} placeholder="Next day · Tue/Fri" className="mt-1 bg-white border-[#E6E0D4]" />
            </div>
          </div>
        </div>
        <DialogFooter className="pt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} className="bg-[#0B1F3A] hover:bg-[#11294A] text-white">
            {supplier ? "Save changes" : "Create vendor"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const EMPTY_CATEGORY = { name: "" };

function buildCategoryPayload(form, existing) {
  const nowIso = new Date().toISOString();
  return {
    id: existing?.id || uid("cat"),
    name: form.name.trim(),
    updatedAt: nowIso,
    createdAt: existing?.createdAt || nowIso,
  };
}

export function CategoryDialog({ open, onOpenChange, category, onSave, existing = [] }) {
  const [form, setForm] = useState(EMPTY_CATEGORY);
  useEffect(() => { if (open) setForm(category ? { ...category } : EMPTY_CATEGORY); }, [open, category]);
  const handleSubmit = () => {
    const name = form.name.trim();
    if (!name) return toast.error("Name is required");
    const clash = existing.some(
      (c) => c.id !== category?.id && c.name.toLowerCase() === name.toLowerCase()
    );
    if (clash) return toast.error("A category with that name already exists");
    onSave(buildCategoryPayload(form, category), !!category, category?.name);
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-[#FBF8F1] border-[#E6E0D4]">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl text-[#0B1F3A]">
            {category ? "Edit category" : "New category"}
          </DialogTitle>
          <DialogDescription className="text-[#0B1F3A]/65">
            Categories organise the catalog. Renaming one will update all products that use it.
          </DialogDescription>
        </DialogHeader>
        <div className="pt-2">
          <Label className="text-[10.5px] tracking-eyebrow uppercase text-[#0B1F3A]/60">Name</Label>
          <Input
            data-testid="category-name-input"
            autoFocus
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g., Fruit"
            className="mt-1 bg-white border-[#E6E0D4]"
          />
        </div>
        <DialogFooter className="pt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="category-save-btn" onClick={handleSubmit} className="bg-[#0B1F3A] hover:bg-[#11294A] text-white">
            {category ? "Save changes" : "Create category"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
